import { Link } from "react-router-dom";
import { BookOpen, Calculator, ShieldAlert, ArrowRight } from "lucide-react";

export default function Methodology() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FEF2F2] text-[#C8102E]"><BookOpen className="h-5 w-5" /></span>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Methodology</h1>
      </div>

      <p className="mt-6 text-lg leading-relaxed text-slate-600">
        PR Pathfinder translates one structured profile into estimated points and eligibility across major
        Canadian Provincial Nominee Program (PNP) streams, plus a simplified Comprehensive Ranking System (CRS)
        estimate. Here is how each number is produced.
      </p>

      <Section title="Language conversion">
        You enter IELTS General, CELPIP General or PTE Core scores. CELPIP levels map directly to the Canadian
        Language Benchmark (CLB). IELTS bands and PTE Core scores (10–90) are converted to CLB per ability (listening,
        reading, writing, speaking) using IRCC's equivalency tables. Your lowest ability determines your overall CLB, which drives most language
        requirements.
      </Section>

      <Section title="Provincial point grids">
        Streams that rank candidates on their own grids — British Columbia's SIRS, Saskatchewan's SINP grid, and
        Manitoba's MPNP assessment — are scored factor by factor (education, work experience, language, age, job
        offer and provincial connection). Each factor's allocation is defined in the editable rules configuration
        and shown transparently on the <Link to="/rules" className="font-semibold text-[#C8102E] hover:underline">Rules Engine</Link> page.
      </Section>

      <Section title="Express Entry-aligned streams">
        Several streams (Alberta Express Entry, Nova Scotia, New Brunswick and PEI) draw candidates from the federal
        Express Entry pool. For these, your CRS estimate is the ranking score, compared against a representative
        recent draw cutoff. Cutoffs change frequently and are shown only as a guide.
      </Section>

      <Section title="Employer job-offer streams (Ontario Workforce Priority)">
        Ontario's single OINP worker pathway is employer-initiated: the employer registers the job offer, then you file
        an Expression of Interest scored on the job's TEER category and NOC group, hourly wage, months worked in the
        position, Canadian earnings, legal status, education, language and job location outside Toronto. Requirements
        for experience, language and education vary by TEER category; we apply the general rules and do not model
        licensed-occupation or skilled-trade exceptions.
      </Section>

      <Section title="CRS estimate">
        The CRS figure follows the official Comprehensive Ranking System: core human capital factors (age, education,
        first-language ability, Canadian work experience), spouse factors where applicable, skill-transferability
        combinations (education, foreign experience and trade certificates paired with language or Canadian experience,
        up to 100 points) and additional points (Canadian study, a sibling in Canada, strong French, and a valid job
        offer). A provincial nomination adds a further 600 points on top. Years of experience are entered as whole
        numbers, so part-year experience rounds down.
      </Section>

      <Section title="Eligibility logic">
        Each stream defines a set of conditions marked <strong>hard</strong> (must be met) or <strong>soft</strong>
        {" "}(competitive / recommended). If any hard requirement is unmet, or the program is paused, the result is
        <strong> Not Eligible</strong>. If only soft requirements are unmet, the result is
        <strong> Potentially Eligible</strong>. When everything is satisfied, the result is <strong>Eligible</strong>.
        Every result lists the criteria met and what is missing.
      </Section>

      <div id="disclaimer" className="mt-12 rounded-xl border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-center gap-2 text-amber-800">
          <ShieldAlert className="h-5 w-5" />
          <h2 className="font-heading text-lg font-bold">Disclaimer</h2>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-amber-900">
          This tool provides simplified estimates for informational purposes only. It is <strong>not legal advice</strong>
          {" "}and is <strong>not affiliated with</strong> the Government of Canada or any provincial government.
          Program criteria, point allocations and draw cutoffs change frequently and may differ from the values used
          here. Always verify your eligibility with official government sources or a licensed immigration
          representative (RCIC or lawyer) before making any decisions.
        </p>
      </div>

      <div className="mt-10 flex justify-center">
        <Link to="/assessment" className="inline-flex items-center gap-2 rounded-md bg-[#C8102E] px-6 py-3 text-sm font-semibold text-white hover:bg-[#A60C24]">
          Start your assessment <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-slate-900">
        <Calculator className="h-5 w-5 text-[#C8102E]" /> {title}
      </h2>
      <p className="mt-2 leading-relaxed text-slate-600">{children}</p>
    </div>
  );
}
